import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { BRAND } from '../../constants/branding';
import TenantHubLogo from '../../components/TenantHubLogo';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setEmailError('');
    setPasswordError('');

    const trimmedEmail = String(email || '').trim();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!trimmedEmail || !emailPattern.test(trimmedEmail)) {
      setEmailError('Invalid email address. Please check the Email Address field.');
      setError('Please correct the highlighted Email Address field.');
      setLoading(false);
      return;
    }
    if (!password) {
      setPasswordError('Password is required. Please check the Password field.');
      setError('Please correct the highlighted Password field.');
      setLoading(false);
      return;
    }

    try {
      const result = await login(trimmedEmail, password);
      
      if (result.success && result.data.requireMFA) {
        navigate('/verify-mfa', {
          state: {
            sessionId: result.data.sessionId,
            email: result.data.email,
            stepUp: result.data.stepUp,
            step: result.data.step,
            totalSteps: result.data.totalSteps,
            availableMethods: result.data.availableMethods,
            currentMethod: result.data.currentMethod
          }
        });
      } else if (result.success && result.data.requireOTP) {
        navigate('/verify-otp', {
          state: {
            sessionId: result.data.sessionId,
            adminId: result.data.adminId,
            email: result.data.email
          }
        });
      } else if (result.success && result.data?.token) {
        if (result.data.requirePasswordChange || result.data.admin?.must_change_password) {
          navigate('/change-password-required');
        } else {
          navigate('/');
        }
      }
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.code === 'IP_NOT_ALLOWED') {
        navigate('/access-denied', {
          replace: true,
          state: {
            ip: err.response.data.ip || '',
            message: err.response.data.message || '',
            detail: err.response.data.detail || '',
            reason: err.response.data.reason || ''
          }
        });
        return;
      }
      const apiMessage = err.response?.data?.message || 'Login failed';
      const apiField = err.response?.data?.field;

      if (apiField === 'email') {
        setEmailError(apiMessage);
        setError('Invalid email address. Please check the Email Address field.');
      } else if (apiField === 'password') {
        setPasswordError(apiMessage);
        setError('Invalid password. Please check the Password field.');
      } else {
        if (/email/i.test(apiMessage) && !/password/i.test(apiMessage)) {
          setEmailError(apiMessage);
          setError('Invalid email address. Please check the Email Address field.');
        } else if (/password/i.test(apiMessage)) {
          setPasswordError(apiMessage);
          setError('Invalid password. Please check the Password field.');
        } else {
          setError(apiMessage);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        px: 2
      }}
    >
      <Card
        sx={{
          maxWidth: 450,
          width: '100%',
          bgcolor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Logo */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <TenantHubLogo size={72} gradient="linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)" />
            </Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {BRAND.loginTitle}
            </Typography>
            <Typography variant="body1" sx={{ color: '#e2e8f0', mt: 1, fontWeight: 600 }}>
              {BRAND.tagline}
            </Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5, px: 1 }}>
              {BRAND.catchPhrase}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              label="Email Address"
              placeholder="Please enter your email ID"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
                setEmailError('');
              }}
              required
              error={Boolean(emailError)}
              helperText={emailError}
              autoComplete="username"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255,255,255,0.05)',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                  '&.Mui-focused fieldset': { borderColor: '#f59e0b' }
                },
                '& .MuiInputLabel-root': { color: '#94a3b8' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#f59e0b' },
                '& .MuiOutlinedInput-input': {
                  color: '#fff',
                  '&::placeholder': { color: '#64748b', opacity: 1 }
                },
                // Keep dark theme when the browser autofills credentials
                '& .MuiOutlinedInput-input:-webkit-autofill, & .MuiOutlinedInput-input:-webkit-autofill:hover, & .MuiOutlinedInput-input:-webkit-autofill:focus, & .MuiOutlinedInput-input:-webkit-autofill:active': {
                  WebkitTextFillColor: '#fff',
                  caretColor: '#fff',
                  WebkitBoxShadow: '0 0 0 1000px #1e293b inset',
                  boxShadow: '0 0 0 1000px #1e293b inset',
                  transition: 'background-color 99999s ease-in-out 0s'
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: '#94a3b8' }} />
                  </InputAdornment>
                )
              }}
            />

            <TextField
              fullWidth
              label="Password"
              placeholder="Please enter your password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
                setPasswordError('');
              }}
              required
              error={Boolean(passwordError)}
              helperText={passwordError}
              autoComplete="current-password"
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255,255,255,0.05)',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                  '&.Mui-focused fieldset': { borderColor: '#f59e0b' }
                },
                '& .MuiInputLabel-root': { color: '#94a3b8' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#f59e0b' },
                '& .MuiOutlinedInput-input': {
                  color: '#fff',
                  '&::placeholder': { color: '#64748b', opacity: 1 }
                },
                '& .MuiOutlinedInput-input:-webkit-autofill, & .MuiOutlinedInput-input:-webkit-autofill:hover, & .MuiOutlinedInput-input:-webkit-autofill:focus, & .MuiOutlinedInput-input:-webkit-autofill:active': {
                  WebkitTextFillColor: '#fff',
                  caretColor: '#fff',
                  WebkitBoxShadow: '0 0 0 1000px #1e293b inset',
                  boxShadow: '0 0 0 1000px #1e293b inset',
                  transition: 'background-color 99999s ease-in-out 0s'
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: '#94a3b8' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      sx={{ color: '#94a3b8' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                py: 1.5,
                background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                fontWeight: 600,
                fontSize: '1.1rem',
                '&:hover': {
                  background: 'linear-gradient(135deg, #d97706 0%, #dc2626 100%)'
                }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
            </Button>
          </form>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              component={RouterLink}
              to="/signup"
              sx={{ color: '#f59e0b', textTransform: 'none', mb: 1 }}
            >
              New customer? Tenant Signup →
            </Button>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
              Access restricted to authorized Super Administrators only
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
